// ============================================================
// openLink.ts
// Helper unique pour ouvrir un lien provenant d'une notification.
//
// Gère 3 cas :
//   1. URL absolue de la même origine (ex: "http://localhost:3000/utilisateurs")
//      → extrait le path et utilise navigate() (SPA, pas de full reload)
//   2. URL absolue d'une autre origine (https://google.com, mailto:, tel:, etc.)
//      → window.open dans un nouvel onglet
//   3. Chemin relatif ("/utilisateurs", "utilisateurs", "/article/42")
//      → ajoute le slash de tête si manquant et utilise navigate()
//
// Évite le bug "/utilisateurs/http://localhost:3000/utilisateurs" causé par
// react-router qui traite les URL absolues comme des chemins relatifs.
// ============================================================

import type { NavigateFunction } from 'react-router-dom';

const ABSOLUTE_URL_RE = /^[a-z][a-z0-9+.-]*:\/\//i;   // http://, https://, ws://, etc.
const SCHEME_NO_HOST_RE = /^(mailto|tel|sms):/i;       // mailto:, tel:, sms:

export function openLink(lien: string, navigate: NavigateFunction): void {
  if (!lien) return;

  const trimmed = lien.trim();

  // Schemes sans host (mailto/tel/sms) → toujours ouvrir hors SPA
  if (SCHEME_NO_HOST_RE.test(trimmed)) {
    window.open(trimmed, '_blank', 'noopener,noreferrer');
    return;
  }

  // URL absolue (http://, https://, etc.)
  if (ABSOLUTE_URL_RE.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (url.origin === window.location.origin) {
        // Même domaine → garder la SPA, naviguer en interne
        navigate(url.pathname + url.search + url.hash);
        return;
      }
      // Autre domaine → nouvel onglet
      window.open(trimmed, '_blank', 'noopener,noreferrer');
      return;
    } catch {
      // URL invalide — fallback en navigation interne (probablement un path mal formé)
    }
  }

  // Chemin relatif — garantir le slash de tête pour react-router
  navigate(trimmed.startsWith('/') ? trimmed : `/${trimmed}`);
}
